import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { CheckCircle, XCircle, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export default function TimesheetApproval() {
  const [timesheets, setTimesheets] = useState([]);
  const [statusFilter, setStatusFilter] = useState('SUBMITTED');
  const [search, setSearch] = useState('');
  const [approveConfirm, setApproveConfirm] = useState(null);
  const [rejectConfirm, setRejectConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = async () => {
    try {
      const url = statusFilter === 'all' ? '/timesheets' : `/timesheets?status_filter=${statusFilter}`;
      const res = await api.get(url);
      setTimesheets(res.data.data || []);
    } catch (e) { toast.error('Failed to load timesheets'); }
  };

  useEffect(() => { fetchData(); }, [statusFilter]); // eslint-disable-line

  const handleApprove = async () => {
    if (!approveConfirm) return;
    setActionLoading(true);
    try {
      const res = await api.post(`/timesheets/${approveConfirm.id}/approve`);
      const ledger = res.data.ledger;
      toast.success(`Approved! Deducted $${ledger?.amount || 0} from ${ledger?.category || 'CORE'}`);
      setApproveConfirm(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectConfirm) return;
    setActionLoading(true);
    try {
      await api.post(`/timesheets/${rejectConfirm.id}/reject`, { reason: 'Rejected by admin' });
      toast.success('Timesheet rejected');
      setRejectConfirm(null); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Rejection failed'); }
    finally { setActionLoading(false); }
  };

  const filtered = timesheets.filter(ts => {
    if (!search) return true;
    return ts.staff_name?.toLowerCase().includes(search.toLowerCase()) ||
           ts.participant_name?.toLowerCase().includes(search.toLowerCase()) ||
           ts.date?.includes(search);
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Timesheet Approval</h1>
        <p className="text-muted-foreground mt-1">Review and approve submitted timesheets</p>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 md:w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by staff, participant, date..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="timesheet-search" />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px]" data-testid="timesheet-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="SUBMITTED">Submitted</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="REJECTED">Rejected</SelectItem>
              <SelectItem value="INVOICED">Invoiced</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <p className="text-sm text-muted-foreground">{filtered.length} timesheet(s)</p>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Staff</TableHead>
                <TableHead>Participant</TableHead>
                <TableHead>Service</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No timesheets found</TableCell></TableRow>
              ) : filtered.map(ts => (
                <TableRow key={ts.id} className="hover:bg-muted/60" data-testid={`timesheet-row-${ts.id}`}>
                  <TableCell className="font-mono text-xs tabular-nums">{ts.date}</TableCell>
                  <TableCell className="font-medium">{ts.staff_name || 'N/A'}</TableCell>
                  <TableCell>{ts.participant_name || 'N/A'}</TableCell>
                  <TableCell className="text-sm">{ts.service_name || 'N/A'}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{ts.hours_worked}h</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">${ts.rate_per_unit || 0}/h</TableCell>
                  <TableCell><StatusBadge status={ts.status} /></TableCell>
                  <TableCell>
                    {ts.status === 'SUBMITTED' && (
                      <div className="flex items-center gap-1">
                        <Button size="sm" onClick={() => setApproveConfirm(ts)} data-testid={`approve-timesheet-${ts.id}`}>
                          <CheckCircle className="h-3 w-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setRejectConfirm(ts)} data-testid={`reject-timesheet-${ts.id}`}>
                          <XCircle className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approve Confirmation */}
      <ConfirmDialog
        open={!!approveConfirm}
        onOpenChange={() => setApproveConfirm(null)}
        title="Approve Timesheet"
        description={approveConfirm ? `Approve ${approveConfirm.hours_worked}h for ${approveConfirm.staff_name || 'staff'} on ${approveConfirm.date}? This will deduct from the participant's funding plan.` : ''}
        confirmLabel="Approve Timesheet"
        loading={actionLoading}
        onConfirm={handleApprove}
      />

      {/* Reject Confirmation */}
      <ConfirmDialog
        open={!!rejectConfirm}
        onOpenChange={() => setRejectConfirm(null)}
        title="Reject Timesheet"
        description={rejectConfirm ? `Reject timesheet for ${rejectConfirm.staff_name || 'staff'} on ${rejectConfirm.date}?` : ''}
        confirmLabel="Reject"
        variant="destructive"
        loading={actionLoading}
        onConfirm={handleReject}
      />
    </div>
  );
}
